import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  DataUsage as DataUsageIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const AgentPerformance = ({ agent }) => {
  const performance = agent.performance || {};

  const getPerformanceColor = (value, threshold = 80) => {
    if (value >= threshold) return 'success';
    if (value >= threshold * 0.7) return 'warning';
    return 'error';
  };

  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    if (hours < 24) return `${Math.round(hours)} hours`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days} days ${remainingHours > 0 ? `${Math.round(remainingHours)} hours` : ''}`;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <AnalyticsIcon sx={{ mr: 1 }} />
        Performance Analytics
      </Typography>
      
      <Grid container spacing={3}>
        {/* Task Completion Rate */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ mr: 1 }} />
                Task Completion Rate
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={performance.successRate || 100} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  mb: 2,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getPerformanceColor(performance.successRate || 100) === 'success' ? '#4caf50' : 
                                   getPerformanceColor(performance.successRate || 100) === 'warning' ? '#ff9800' : '#f44336'
                  }
                }}
              />
              <Typography variant="body2" sx={{ mb: 1 }}>
                {performance.successRate || 100}% success rate
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on {performance.tasksCompleted || 0} completed tasks
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TimelineIcon sx={{ mr: 1 }} />
                Activity Summary
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Tasks Completed"
                    secondary={performance.tasksCompleted || 0}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Hours Worked"
                    secondary={formatDuration(performance.hoursWorked || 0)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SpeedIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Average Response Time"
                    secondary={`${performance.averageResponseTime || 0}s`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DataUsageIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Interactions"
                    secondary={performance.totalInteractions || 0}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 1 }} />
                Performance Metrics
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {performance.tasksCompleted || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tasks Completed
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="h4" color="success" gutterBottom>
                      {performance.successRate || 100}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="h4" color="info" gutterBottom>
                      {performance.averageResponseTime || 0}s
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Response Time
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="h4" color="secondary" gutterBottom>
                      {performance.totalInteractions || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Interactions
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                Recent Activity
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Last Active
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {performance.lastActive ? new Date(performance.lastActive).toLocaleString() : 'Never'}
                    </Typography>
                  </Box>
                  <Chip 
                    label={agent.status} 
                    color={agent.status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Agent Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {agent.isConfigured ? 'Fully Configured' : 'Needs Configuration'}
                    </Typography>
                  </Box>
                  <Chip 
                    label={agent.isConfigured ? 'Configured' : 'Pending'} 
                    color={agent.isConfigured ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Hired Date
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(agent.hireDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Chip 
                    label="Active" 
                    color="success"
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AgentPerformance; 